import { z } from "zod"

import { LoanProvider } from "../constants/"
import type { GoodleapPayment } from "../validation"

export const PrettyProviderName: Record<LoanProvider | `-`, string> = {
  [LoanProvider.concert]: `Concert`,
  [LoanProvider.credit_human]: `Credit Human`,
  [LoanProvider.goodleap]: `GoodLeap`,
  [LoanProvider.mosaic]: `Mosaic`,
  [LoanProvider.sunlight]: `Sunlight Financial`,
  [LoanProvider.sungage]: `Sungage Financial`,
  [LoanProvider.enfin]: `Enfin`,
  [LoanProvider.lightreach]: `LightReach`,
  "-": ``,
}

export const GOODLEAP_CATEGORIES: Record<string, string> = {
  "e69c0afc-e3a1-4175-ad2f-a745ed68861b": `BATTERY`,
  "05985541-97aa-40e1-b9d0-576c2a4f5015": `SOLAR`,
  "541f4a57-63c6-4f24-a75f-9edb926380c1": `SOLAR`,
  "5bd7cdcf-5ccf-4693-8541-0f118f3cd96b": `BATTERY`,
} as const

export const goodleapMetaSchema = z.object({
  id: z.string(),
  name: z.string(),
  rate: z.number(),
  term: z.number(),
  categoryId: z.string(),
  productCode: z.string(),
  promotionIds: z.array(z.string()),
  channels: z.array(z.string()),
  states: z.array(z.string()),
  meta: z.object({ cursor: z.string().nullable() }).optional(),
  dealerFee: z.number(),
  estimatedFirstPaymentMonth: z.number().optional(),
  productDescription: z.string().optional(),
  productId: z.string().optional(),
  productName: z.string().optional(),
  productPromotionType: z.string().nullable().optional(),
  productPromotionalPeriod: z
    .object({
      duration: z.number(),
      unit: z.string(),
    })
    .nullable()
    .optional(),
})

export const goodleapPresentationMetaSchema = goodleapMetaSchema.pick({
  productCode: true,
  productPromotionalPeriod: true,
})

export type GoodLeapMonthsLoan = {
  provider?: string | null
  term: number
  providerMeta?: unknown
}

export function getGoodLeapMonths(
  payment: GoodleapPayment,
  payments: GoodleapPayment[],
  selectedLoan: GoodLeapMonthsLoan | undefined,
): { startMonth: number; endMonth: number } {
  if (selectedLoan?.provider !== `goodleap`) {
    return { startMonth: 0, endMonth: 0 }
  }

  const termMonths = selectedLoan.term * 12
  const parsedMeta = goodleapPresentationMetaSchema.parse(
    selectedLoan.providerMeta,
  )
  const endOfPromotion = parsedMeta.productPromotionalPeriod?.duration ?? 18
  const beginningOfAdjusted = endOfPromotion + 1
  const endOfAdjusted = payments.length === 3 ? 18 : termMonths
  const beginningOfPaydown =
    payments.length === 2 ? endOfPromotion + 1 : endOfAdjusted + 1

  switch (payment.type) {
    case `INITIAL`:
      return { startMonth: 1, endMonth: endOfPromotion }
    case `ADJUSTED`:
      return { startMonth: beginningOfAdjusted, endMonth: endOfAdjusted }
    case `ADJUSTED_W_PAYDOWN`:
    case `ADJUSTED_WO_PAYDOWN`:
      return { startMonth: beginningOfPaydown, endMonth: termMonths }
    default:
      console.error(`Unknown payment type: ${payment.type}`)

      return { startMonth: Infinity, endMonth: Infinity }
  }
}
